import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { CircularProgress, Typography } from '@mui/material';
import NormalProjectIcon from '@src/assets/icons/project-normal.svg';
import PresentationProjectIcon from '@src/assets/icons/project-presentation.svg';
import { MAX_FILE_NAME_LENGTH } from '@src/constants/dubbing';
import { isValidFile } from '@src/utils/checkValid';
import { formatFileSize } from '@src/utils/file';
import { MEGA_BYTE } from '@src/constants/upload';
import { COLOR } from '@src/styles/color';
import { PRESENTATION_FILE_FORMAT } from '@src/constants/presentation';
import CommonButton from '@src/new-components/Button';
import Tooltip from '@src/components/Tooltip';
import { StyledFileDropzone } from './index.style';

export default function FileDropzoneV3({
  fileType,
  onAddFile,
  onChangeFile,
  onChangeFileError,
  className = '',
  hasResetFile,
  onChangeHasResetFile,
  loadingGetContentFile,
  dataId = '',
  extraFileDetails = [],
  extraContent = null,
  submitTooltip = null,
  maxSize = MEGA_BYTE,
  maxSizeLabel = null,
}) {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [hightlight, setHightlight] = useState(false);
  const [formatError, setFormatError] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [oversize, setOverSize] = useState(false);
  const fileInputRef = useRef(null);
  const { t } = useTranslation();

  const acceptFileTypes = fileType?.map((type) => `.${type}`)?.join(', ');
  const acceptInputTypes = fileType?.map((type) => `.${type}`)?.join(',');

  const isValidSize = (size) => size <= maxSize;

  const getShortFileName = (value) => {
    if (value && value?.length > MAX_FILE_NAME_LENGTH) {
      return `${value.slice(0, MAX_FILE_NAME_LENGTH - 3)}...`;
    }
    return value;
  };

  const handleReset = () => {
    setFile(null);
    setFileName(null);
    setFormatError(false);
    setOverSize(false);
    setHightlight(false);
    setErrorMessage('');
    if (onChangeFile) onChangeFile(null);
  };

  const handleChangeFileError = () => {
    if (!onChangeFileError) return;
    onChangeFileError();
  };

  const handleChangeFile = (e) => {
    const { files } = e.target;
    handleReset();
    const shortFileName = getShortFileName(files[0]?.name);
    setFileName(shortFileName);
    if (!files || files.length !== 1) return;
    if (!isValidFile(files[0].name, fileType)) {
      setFormatError(true);
      handleChangeFileError();
      return;
    }
    if (!isValidSize(files[0].size)) {
      setOverSize(true);
      handleChangeFileError();
      return;
    }
    setFile(files[0]);
  };

  const handleChangeFileState = async (fileState) => {
    if (!onChangeFile) return;
    try {
      setIsUploadingFile(true);
      await onChangeFile(fileState);
      setIsUploadingFile(false);
    } catch (err) {
      setIsUploadingFile(false);
      setErrorMessage(err.message);
      setFile(null);
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setHightlight(true);
  };

  const onDragLeave = () => setHightlight(false);

  const onDrop = (e) => {
    e.preventDefault();
    handleReset();
    const { files } = e.dataTransfer;
    const shortFileName = getShortFileName(files[0]?.name);
    setFileName(shortFileName);
    if (!files || files.length !== 1) return;
    if (!isValidFile(files[0].name, fileType)) {
      setFormatError(true);
      handleChangeFileError();
      return;
    }
    if (!isValidSize(files[0].size)) {
      setOverSize(true);
      handleChangeFileError();
      return;
    }
    setFile(files[0]);
  };

  const openFileDialog = () => fileInputRef.current.click();

  const handleAddSuccessFile = () => {
    if (onAddFile) onAddFile(file, handleReset);
  };

  useEffect(() => {
    if (hasResetFile) {
      handleReset();
      onChangeHasResetFile(false);
    }
  }, [hasResetFile]);

  useEffect(() => {
    if (file) handleChangeFileState(file);
  }, [file]);

  useEffect(() => {
    const fileInput = fileInputRef.current;
    if (!fileInput) return () => {};
    const handleResetInputValue = () => {
      fileInput.value = null;
    };
    fileInput.addEventListener('click', handleResetInputValue);
    return () => {
      fileInput.removeEventListener('click', handleResetInputValue);
    };
  }, [fileInputRef]);

  const isError = formatError || oversize || errorMessage;

  const getErrorText = () => {
    if (formatError) return t('fileFormatError', { fileType: acceptFileTypes });
    if (oversize) return t('oversizeError');
    if (errorMessage) return errorMessage;
    return '';
  };

  const renderLoadingArea = () => (
    <StyledFileDropzone className={className}>
      <div className="dropzone">
        <CircularProgress />
      </div>
    </StyledFileDropzone>
  );

  const renderUploadArea = () => (
    <StyledFileDropzone className={className} isError={isError}>
      <div
        className={classNames('dropzone', { hightlight })}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        role="presentation"
        onClick={openFileDialog}
      >
        <input
          ref={fileInputRef}
          className="file-input"
          type="file"
          accept={acceptInputTypes}
          onChange={handleChangeFile}
        />
        <CloudUploadRoundedIcon className="upload-icon" />
        <p className="upload-text">{t('dropFile')}</p>
        <div className="upload-description">
          <p>{t('supportedFormats', { fileType: acceptFileTypes })}</p>
          <p>
            {maxSizeLabel ||
              t('maxFileSize', { size: formatFileSize(maxSize) })}
          </p>
        </div>
        {isError && <p className="error-text">{getErrorText()}</p>}
      </div>
    </StyledFileDropzone>
  );

  const renderFileUploaded = () => (
    <StyledFileDropzone className={className}>
      <div className="dropzone uploaded">
        <div className="file-item">
          <div className="file-icon-wrapper">
            <img
              src={
                file?.name?.endsWith(PRESENTATION_FILE_FORMAT)
                  ? PresentationProjectIcon
                  : NormalProjectIcon
              }
              alt="file-icon"
              className="file-icon"
            />
          </div>
          <div className="file-info">
            <Typography
              data-id={dataId}
              data-type-file={
                fileName ? fileName.split('.').pop().toLowerCase() : ''
              }
              className="file-name"
            >
              {fileName}
            </Typography>
            <div className="file-details">
              {extraFileDetails.map((detail, i) => (
                <React.Fragment key={detail}>
                  {i > 0 && <span className="file-details-dot" />}
                  <span>{detail}</span>
                </React.Fragment>
              ))}
              {extraFileDetails.length > 0 && (
                <span className="file-details-dot" />
              )}
              <span>{formatFileSize(file?.size)}</span>
            </div>
          </div>
          <CommonButton
            variant="outlined"
            onClick={handleReset}
            disabled={loadingGetContentFile}
            customStyle={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              border: '0.5px solid rgba(30, 22, 78, 0.16)',
              minWidth: '32px',
              padding: '4px',
            }}
          >
            <DeleteOutlineRoundedIcon
              sx={{ width: 24, height: 24, color: '#1E164ECC' }}
            />
          </CommonButton>
        </div>
      </div>
    </StyledFileDropzone>
  );

  return (
    <>
      {(() => {
        if (isUploadingFile) return renderLoadingArea();
        if (file === null) return renderUploadArea();
        return renderFileUploaded();
      })()}
      {extraContent && extraContent}
      <Tooltip
        placement="top"
        title={submitTooltip || ''}
        arrow
        disableHoverListener={!submitTooltip}
      >
        <div
          style={{
            marginLeft: 'auto',
            marginTop: '16px',
            width: 'fit-content',
          }}
        >
          <CommonButton
            variant="contained"
            color="primary"
            onClick={handleAddSuccessFile}
            disabled={!file || loadingGetContentFile || isUploadingFile}
            customStyle={{
              height: '40px',
              borderRadius: '8px',
              padding: '8px 24px',
            }}
            textProps={{
              variantText: 'body-14-semibold',
              color: COLOR.darkBlue,
            }}
            startIcon={
              loadingGetContentFile && (
                <CircularProgress size={16} sx={{ color: COLOR.darkBlue }} />
              )
            }
          >
            {t('continue')}
          </CommonButton>
        </div>
      </Tooltip>
    </>
  );
}
